import { ShopigurusHTMLElement } from '@shopigurus/base';

const HOVER_OPEN_DELAY = 100;
const HOVER_CLOSE_DELAY = 80;
/* Editor-only: when a section setting changes, Shopify rewrites the section
   HTML and the dialog/panel state is lost. We stash "drawer was open with
   category X" in sessionStorage so the new instance can restore it within
   the same editor tab. Key is per-section-id so multiple headers on a page
   don't clash. */
const EDITOR_STATE_KEY = (sectionId) => `shpflt-header:editor-state:${sectionId}`;

class ShpfltHeader extends ShopigurusHTMLElement {
  #openCategoryId = null;
  #openTimer = null;
  #closeTimer = null;
  #reducedMotion = false;
  #isEditor = false;
  #desktopMQ = null;
  #scrollHandler = null;
  #transparentObserver = null;
  #transparentVisibleCount = 0;
  #lastScrollY = 0;
  #resizeObserver = null;
  #connectedAt = 0;
  #promoAutoplayInterval = null;

  connectedCallback() {
    super.connectedCallback();

    this.#connectedAt = Date.now();
    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#isEditor = !!(window.Shopify && window.Shopify.designMode);

    // Teleport announcement blocks into the promo bar first, then menu-item
    // parts into nav / drawer. Both MUST run before any querySelector below.
    this.#teleportAnnouncements();
    // Teleport menu-item parts (desktop category, drawer tab, drawer panel)
    // out of the hidden source div into their real destinations. MUST run
    // before any querySelector below — those queries assume the final DOM.
    this.#teleportMenuItems();

    const { signal } = this.abortController;

    // Desktop mega-menu triggers
    this.querySelectorAll('[data-shpflt-trigger]').forEach((trigger) => {
      const category = trigger.closest('[data-shpflt-category]');
      if (!category) return;
      const categoryId = category.dataset.categoryId;

      // Hover: open with debounce (skip in editor — clicks/keyboard only)
      if (!this.#isEditor) {
        trigger.addEventListener('mouseenter', () => this.#scheduleOpen(categoryId), { signal });
      }
      trigger.addEventListener('focus', () => this.#openCategory(categoryId), { signal });
      trigger.addEventListener('click', (event) => {
        // When the trigger is a real link (top-level item with a destination),
        // let the click navigate to its page. Hover/focus still opens the mega
        // panel. In the editor we never navigate away — always toggle so
        // merchants can inspect the panel.
        const href = trigger.getAttribute('href');
        const isLink = trigger.tagName === 'A' && href && href !== '#';
        if (isLink && !this.#isEditor) return;
        event.preventDefault();
        event.stopPropagation();
        if (this.#openCategoryId === categoryId) {
          this.#closeAll();
        } else {
          this.#openCategory(categoryId);
        }
      }, { signal });
    });

    // Keep panel open while pointer is over it (skip in editor)
    if (!this.#isEditor) {
      this.querySelectorAll('[data-shpflt-category]').forEach((category) => {
        category.addEventListener('mouseenter', () => this.#cancelClose(), { signal });
        category.addEventListener('mouseleave', () => this.#scheduleClose(), { signal });
      });
    }

    // Close on Escape
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.#openCategoryId) {
        this.#closeAll();
        const trigger = this.querySelector(`[data-shpflt-trigger][data-target-panel*="${this.#openCategoryId}"]`);
        trigger?.focus();
      }
    }, { signal });

    // Close on focus leaving the header
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.#closeAll();
    }, { signal });

    // Close on outside click (skip in editor — clicks elsewhere may be panel inspection)
    if (!this.#isEditor) {
      document.addEventListener('click', (event) => {
        if (this.#openCategoryId && !this.contains(event.target)) this.#closeAll();
      }, { signal });
    }

    // Backdrop click closes
    this.querySelector('[data-shpflt-backdrop]')?.addEventListener('click', () => this.#closeAll(), { signal });

    // ── Editor: click anywhere inside a panel keeps it open ──────────────
    if (this.#isEditor) {
      this.querySelectorAll('[data-shpflt-panel]').forEach((panel) => {
        panel.addEventListener('click', () => {
          const category = panel.closest('[data-shpflt-category]');
          if (category) this.#openCategory(category.dataset.categoryId);
        }, { signal });
      });

      // ── Editor: ensure the first category opens automatically when section selected ──
      // shopify:section:select is dispatched by ShopigurusHTMLElement → onSectionSelect()
    }

    // ── Mobile drawer ────────────────────────────────────────────────────
    const drawer = this.querySelector('[data-shpflt-drawer]');
    const openTriggers = this.querySelectorAll('[data-shpflt-drawer-open]');
    const closeTriggers = this.querySelectorAll('[data-shpflt-drawer-close]');

    openTriggers.forEach((t) => {
      t.addEventListener('click', () => {
        if (typeof drawer?.showModal === 'function') {
          drawer.showModal();
        } else if (drawer) {
          drawer.setAttribute('open', '');
        }
        t.setAttribute('aria-expanded', 'true');
        this.#lockBodyScroll();
        /* Remember "drawer open" in editor mode so a section reload after a
           setting change re-opens it. Picks the currently-selected drawer
           tab so the restore lands on the same panel. */
        if (this.#isEditor) {
          const active = this.querySelector('[data-shpflt-drawer-tab][aria-selected="true"]')
            || this.querySelector('[data-shpflt-drawer-tab]');
          this.#writeEditorState({ open: true, category: active?.dataset.categoryId });
        }
      }, { signal });
    });

    closeTriggers.forEach((t) => {
      t.addEventListener('click', () => drawer?.close?.(), { signal });
    });

    drawer?.addEventListener('close', () => {
      openTriggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      this.#unlockBodyScroll();
      // Reset any in-flight drag offset so a re-open starts at rest.
      drawer.style.removeProperty('--shpflt-drawer-drag');
      drawer.removeAttribute('data-dragging');
      /* User-driven close: forget the editor-restore marker so the drawer
         does not re-appear after the next setting change. The close event
         fires for both close button, knob dismiss, ESC, and dialog backdrop. */
      this.#clearEditorState();
    }, { signal });

    drawer?.addEventListener('click', (event) => {
      if (event.target === drawer) drawer.close();
    }, { signal });

    // ── Bottom-sheet knob: drag-to-dismiss ───────────────────────────────
    if (this.dataset.mobileDrawerStyle === 'bottom_sheet') {
      this.#bindKnobDrag(drawer, signal);
    }

    // ── Horizontal rails: drop the right-edge fade-mask once scrolled to
    //   the last item. Toggles [data-at-end]; CSS uses it to remove the mask. */
    this.#bindRailEdgeFade(signal);

    // ── Drawer utility locale toggle: opens/closes the locale options list. */
    this.#bindLocaleToggles(signal);

    // ── Search icon toggle: shows/hides the inline search form. */
    this.#bindSearchToggle(signal);

    // ── Desktop bar locale dropdown: custom popup over hidden native select. */
    this.#bindBarLocaleDropdown(signal);

    // Drawer category tabs
    this.querySelectorAll('[data-shpflt-drawer-tab]').forEach((tab) => {
      tab.addEventListener('click', () => this.#switchDrawerCategory(tab.dataset.categoryId), { signal });
    });

    // Keyboard tab navigation (Left/Right arrows) per WAI-ARIA tablist
    const tabsContainer = this.querySelector('.shpflt-header__drawer-tabs');
    tabsContainer?.addEventListener('keydown', (event) => {
      const tabs = [...tabsContainer.querySelectorAll('[data-shpflt-drawer-tab]')];
      const currentIndex = tabs.indexOf(document.activeElement);
      if (currentIndex === -1) return;
      let next = currentIndex;
      if (event.key === 'ArrowRight') next = (currentIndex + 1) % tabs.length;
      else if (event.key === 'ArrowLeft') next = (currentIndex - 1 + tabs.length) % tabs.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = tabs.length - 1;
      else return;
      event.preventDefault();
      tabs[next].focus();
      this.#switchDrawerCategory(tabs[next].dataset.categoryId);
    }, { signal });

    // ── Breakpoint cross-over: reset state when crossing the desktop boundary ──
    // Mirrors the 990px @container query that swaps mobile drawer ↔ desktop mega-menu.
    // Without this, opening the mobile drawer and resizing into desktop leaves
    // <dialog> in modal-open state, blocking the page until F5.
    this.#desktopMQ = window.matchMedia('(min-width: 990px)');
    const onBreakpoint = (e) => {
      if (e.matches) {
        // Entering desktop: dismiss any open mobile drawer
        if (drawer?.open) drawer.close();
      } else {
        // Entering mobile: collapse any open desktop mega-menu. The drawer is
        // not auto-opened here — same reason as the connectedCallback comment.
        this.#closeAll();
      }
      // Re-evaluate the desktop-only second-column alignment on either side.
      this.#alignMegaColumns();
    };
    this.#desktopMQ.addEventListener('change', onBreakpoint, { signal });

    // (No unconditional drawer auto-open here — that caused the drawer to pop
    //  on every F5 in editor + mobile, even when only the SECTION was selected.
    //  The drawer now opens on demand from `onBlockSelect` when a nested block
    //  is genuinely selected, matching the desktop behaviour.)

    // ── Editor: restore state across section reloads (setting changes) ───
    // Shopify replaces the section's HTML on every setting change. If the
    // merchant had the mega-menu open (desktop) or the drawer open (mobile)
    // they expect to keep working — without this, every slider tweak collapses
    // the menu and forces them to reopen it.
    if (this.#isEditor) {
      const state = this.#readEditorState();
      if (state?.open && state.category) {
        // Defer one frame so teleport-mounted nodes are settled before
        // #openCategory queries the desktop panel + drawer tab.
        requestAnimationFrame(() => {
          this.#openCategory(state.category);
          const isMobileView = !this.#desktopMQ?.matches;
          if (isMobileView && drawer && !drawer.open) {
            drawer.setAttribute('open', '');
            this.#switchDrawerCategory(state.category);
          }
        });
      }
    }

    // ── Scroll behaviour: data-scrolled / data-hidden / data-compact ─────
    this.#initScrollBehaviour(signal);

    // ── Transparent header: observe opt-in section triggers ──────────────
    this.#initTransparencyObserver();

    // ── Publish rendered height to body `--space-top` ───────────────────
    // The collection's `.shpflt-collectionbar` (and other sections) use
    // `top: var(--space-top)` to sit directly below the header. Republish
    // on init, on viewport resize, and on scroll (compact mode changes height).
    this.#publishHeight();
    // Align the mega-menu's second column under the first nav link (the logo
    // width is dynamic, so it can't be expressed in pure CSS). Defer one frame
    // so the logo image + nav have their final rendered widths.
    requestAnimationFrame(() => this.#alignMegaColumns());
    if (typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(() => {
        this.#publishHeight();
        this.#alignMegaColumns();
      });
      this.#resizeObserver.observe(this);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    if (this.#promoAutoplayInterval !== null) {
      clearInterval(this.#promoAutoplayInterval);
      this.#promoAutoplayInterval = null;
    }
    if (this.#transparentObserver) {
      this.#transparentObserver.disconnect();
      this.#transparentObserver = null;
    }
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }
  }

  /**
   * Publish the header's rendered height as `--space-top` on <body>, AND
   * reserve matching `padding-top` on <body> when the header is taken out
   * of normal flow (sticky / fixed / scroll-aware). Transparent mode opts
   * out of the reservation so a hero/banner can sit underneath the header.
   *
   * Re-runs on init, viewport resize (ResizeObserver), scroll (compact mode
   * changes height), and whenever the transparency observer flips state.
   */
  #publishHeight() {
    const bar = this.querySelector('[data-shpflt-bar]');
    const h = Math.round((bar || this).getBoundingClientRect().height);
    if (h <= 0) return;

    // Include the promo bar height in the top-space reservation so the first
    // content section never sits under the announcement bar.
    const promoBar = this.querySelector('[data-shpflt-promo-bar]');
    if (promoBar && !promoBar.hidden) {
      const ph = Math.round(promoBar.getBoundingClientRect().height);
      if (ph > 0) {
        document.body.style.setProperty('--space-top', `${h + ph}px`);
      } else {
        document.body.style.setProperty('--space-top', `${h}px`);
      }
    } else {
      document.body.style.setProperty('--space-top', `${h}px`);
    }

    // Body padding-top is reserved when the header is taken out of flow.
    // We deliberately DO NOT remove it during transparent mode — toggling it
    // creates a feedback loop with the transparency IntersectionObserver
    // (padding shift moves the trigger across its band → flips transparent →
    // padding shifts again → flicker). Hero sections that want to overlap
    // the transparent header should apply their own
    // `margin-top: calc(-1 * var(--space-top))`.
    // Sticky mode keeps the header in normal flow (the section wrapper is
    // `position: sticky` — see shpflt-header.css), so it reserves its own height
    // and must NOT also get a body padding-top (that would double the gap).
    // Only the out-of-flow modes (fixed / scroll-aware) need the reservation.
    const mode = this.dataset.positionMode;
    const outOfFlow = mode === 'fixed' || mode === 'scroll-aware';
    document.body.style.paddingTop = outOfFlow ? `${h}px` : '';

    // Publish the collection filter bar height (if present) so sections
    // below it can reserve clearance via `--space-collection`. The legacy
    // header used to do this; new header takes over.
    const collectionBar = document.querySelector('.shpflt-collectionbar');
    if (collectionBar) {
      const ch = Math.round(collectionBar.getBoundingClientRect().height);
      if (ch > 0) document.body.style.setProperty('--space-collection', `${ch}px`);
    }
  }

  /**
   * Pin the first mega-menu column to the logo's width so the SECOND column
   * starts directly under the first nav link. Pure CSS can't do this because
   * the logo's rendered width is dynamic. Publishes `--shpflt-header-mega-col1-w`
   * and a `data-mega-aligned` gate on the host; the matching CSS rule then
   * fixes `flex-basis` on the first column. Recomputed on init, resize, and
   * breakpoint changes. No-op (and cleared) below the 990px desktop breakpoint.
   */
  #alignMegaColumns() {
    // Below desktop the mega panel is replaced by the drawer — clear and bail.
    if (this.#desktopMQ && !this.#desktopMQ.matches) {
      this.removeAttribute('data-mega-aligned');
      this.style.removeProperty('--shpflt-header-mega-col1-w');
      return;
    }
    // Merchant opt-out: when "Alinear columnas con el menú" is off, leave the
    // mega panel as a plain grid (no first-column pinning). Unset/empty keeps
    // the legacy always-aligned behaviour so existing instances don't change.
    if (this.getAttribute('data-align-columns-to-nav') === 'false') {
      this.removeAttribute('data-mega-aligned');
      this.style.removeProperty('--shpflt-header-mega-col1-w');
      return;
    }
    const firstLink = this.querySelector('.shpflt-header__nav-trigger');
    const grid = this.querySelector('.shpflt-header__panel-grid');
    if (!firstLink || !grid) return;

    const gridLeft = grid.getBoundingClientRect().left;
    const linkLeft = firstLink.getBoundingClientRect().left;
    const gap = parseFloat(getComputedStyle(grid).columnGap) || 0;
    // First column spans from the grid's left edge up to the first nav link,
    // minus the inter-column gap → the next column lands under that link.
    const width = Math.round(linkLeft - gridLeft - gap);

    if (width > 0) {
      this.style.setProperty('--shpflt-header-mega-col1-w', `${width}px`);
      this.setAttribute('data-mega-aligned', '');
    } else {
      this.removeAttribute('data-mega-aligned');
      this.style.removeProperty('--shpflt-header-mega-col1-w');
    }
  }

  /**
   * Fires when this section is selected in the Shopify theme editor.
   * Intentionally a no-op — Shopify re-fires `section:select` after every
   * setting change, so auto-opening the mega-menu here makes the preview
   * pop open while the merchant is editing section settings. Block-select
   * (onBlockSelect) still opens the right panel when a menu-item, promo card,
   * or bubble is picked from the sidebar; merchants can hover/click any
   * category trigger in the preview to inspect a specific panel manually.
   */
  onSectionSelect() {
    /* Promo bar: pause slider_fade interval while editing so focus is easier. */
    if (this.#promoAutoplayInterval !== null) {
      clearInterval(this.#promoAutoplayInterval);
      this.#promoAutoplayInterval = null;
    }
  }

  onSectionDeselect() {
    this.#closeAll();
  }

  /**
   * Fires when a block (a promo card) is selected in the theme editor.
   * Both the desktop mega-menu and the mobile drawer render a copy of every
   * promo, each carrying `block.shopify_attributes`. We:
   *   1. Find every element for this block id (potentially 2 copies).
   *   2. Open the desktop category panel.
   *   3. If we're at mobile width, open the drawer (non-modal in the editor so
   *      Shopify's selection overlay still works), switch to the right tab.
   *   4. Scroll the visible copy into view.
   */
  onBlockSelect(e) {
    const blockId = e?.detail?.blockId;
    if (!blockId) return;

    // Page-load suppression: Shopify re-fires block:select after F5 to restore
    // selection. We only want to skip THAT specific edge case (the user wasn't
    // actively clicking), not every block:select that arrives soon after a
    // section reload caused by a setting change. The distinction: on a real
    // F5 there is no stored editor state, so the saved state is the marker.
    // - No stored state + within 300ms of connect = page restore → skip
    // - Anything else = treat as a real selection, open the panel
    if (
      Date.now() - this.#connectedAt < 300 &&
      !this.#readEditorState()
    ) return;

    const safeId = (typeof CSS !== 'undefined' && CSS.escape) ? CSS.escape(blockId) : blockId;
    // Primary lookup: explicit `data-shpflt-block-id` rendered by our liquid.
    // Fallbacks cover Shopify's `shopify_attributes` output + plain id attr.
    const blockEls = [
      ...this.querySelectorAll(
        `[data-shpflt-block-id="${blockId}"], [data-shopify-editor-block-id="${blockId}"], [data-block-id="${blockId}"], #${safeId}`,
      ),
    ];
    if (blockEls.length === 0) return;

    const desktopEl = blockEls.find((el) => el.closest('[data-shpflt-category]')) || null;
    const drawerEl = blockEls.find((el) => el.closest('[data-shpflt-drawer-panel]')) || null;
    // Walks up via .closest() so nested blocks (promo-cards group, individual
    // promo, category-bubbles group, individual bubble) all resolve to their
    // ancestor menu-item's category handle. Drawer tabs carry data-category-id
    // directly, so the final fallback covers them.
    const matchHandle =
      desktopEl?.closest('[data-shpflt-category]')?.dataset.categoryId ||
      drawerEl?.closest('[data-shpflt-drawer-panel]')?.dataset.categoryId ||
      blockEls[0]?.closest('[data-category-id]')?.dataset.categoryId;

    if (matchHandle) this.#openCategory(matchHandle);

    // Decide which copy is "live" for the current viewport. Below the desktop
    // breakpoint we want the drawer copy visible so the editor selection ring
    // actually overlaps something the merchant can see.
    const isMobileView = !this.#desktopMQ?.matches;
    const drawer = this.querySelector('[data-shpflt-drawer]');

    if (isMobileView && drawer) {
      // In the editor, open the dialog non-modally so the editor overlay
      // (which lives in the document layer) can paint over it. On the
      // storefront the drawer is already user-driven, so this branch is moot.
      if (!drawer.open) {
        if (this.#isEditor) drawer.setAttribute('open', '');
        else if (typeof drawer.showModal === 'function') {
          try { drawer.showModal(); } catch { /* already open */ }
        }
      }
      if (matchHandle) this.#switchDrawerCategory(matchHandle);
    }

    const visibleEl = isMobileView ? (drawerEl || desktopEl) : (desktopEl || drawerEl);
    requestAnimationFrame(() => {
      visibleEl?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    });
  }

  onBlockDeselect() {
    // Keep the panel open during editor session — easier to inspect.
  }

  #scheduleOpen(categoryId) {
    clearTimeout(this.#closeTimer);
    if (this.#openCategoryId === categoryId) return;
    if (this.#reducedMotion) {
      this.#openCategory(categoryId);
      return;
    }
    clearTimeout(this.#openTimer);
    this.#openTimer = setTimeout(() => this.#openCategory(categoryId), HOVER_OPEN_DELAY);
  }

  #scheduleClose() {
    clearTimeout(this.#openTimer);
    if (this.#reducedMotion) {
      this.#closeAll();
      return;
    }
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => this.#closeAll(), HOVER_CLOSE_DELAY);
  }

  #cancelClose() {
    clearTimeout(this.#closeTimer);
  }

  #openCategory(categoryId) {
    if (!categoryId) return;
    if (this.hasAttribute('data-search-open')) {
      this.removeAttribute('data-search-open');
      const searchToggle = this.querySelector('[data-shpflt-search-toggle]');
      const searchForm = this.querySelector('[data-shpflt-search]');
      searchToggle?.setAttribute('aria-expanded', 'false');
      searchForm?.setAttribute('hidden', '');
    }
    if (this.#openCategoryId && this.#openCategoryId !== categoryId) {
      this.#applyCategoryState(this.#openCategoryId, false);
    }
    this.#applyCategoryState(categoryId, true);
    this.#openCategoryId = categoryId;
    this.setAttribute('data-any-open', 'true');
    this.#writeEditorState({ open: true, category: categoryId });
  }

  #closeAll() {
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    if (this.#openCategoryId) {
      this.#applyCategoryState(this.#openCategoryId, false);
      this.#openCategoryId = null;
    }
    this.removeAttribute('data-any-open');
    this.#clearEditorState();
  }

  /**
   * Editor-only state helpers. sessionStorage persists across the section-load
   * cycle that Shopify performs on every setting change, but does NOT persist
   * across full page reloads (which is desirable — F5 should not auto-pop the
   * menu). No-ops on the live storefront.
   */
  #readEditorState() {
    if (!this.#isEditor) return null;
    try {
      const raw = sessionStorage.getItem(EDITOR_STATE_KEY(this.dataset.sectionId));
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }
  #writeEditorState(state) {
    if (!this.#isEditor) return;
    try { sessionStorage.setItem(EDITOR_STATE_KEY(this.dataset.sectionId), JSON.stringify(state)); } catch { /* quota / private mode — fine */ }
  }
  #clearEditorState() {
    if (!this.#isEditor) return;
    try { sessionStorage.removeItem(EDITOR_STATE_KEY(this.dataset.sectionId)); } catch { /* fine */ }
  }

  #applyCategoryState(categoryId, isOpen) {
    const category = this.querySelector(`[data-shpflt-category][data-category-id="${categoryId}"]`);
    if (!category) return;
    const trigger = category.querySelector('[data-shpflt-trigger]');
    const panel = category.querySelector('[data-shpflt-panel]');
    category.setAttribute('data-open', String(isOpen));
    trigger?.setAttribute('aria-expanded', String(isOpen));
    if (panel) {
      if (isOpen) panel.removeAttribute('hidden');
      else panel.setAttribute('hidden', '');
    }
  }

  #switchDrawerCategory(categoryId) {
    if (!categoryId) return;
    this.querySelectorAll('[data-shpflt-drawer-tab]').forEach((tab) => {
      const isActive = tab.dataset.categoryId === categoryId;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });
    this.querySelectorAll('[data-shpflt-drawer-panel]').forEach((panel) => {
      if (panel.dataset.categoryId === categoryId) panel.removeAttribute('hidden');
      else panel.setAttribute('hidden', '');
    });
    /* Keep editor state in sync so a setting change while the drawer is
       open on mobile preserves the active tab. */
    this.#writeEditorState({ open: true, category: categoryId });
  }

  /**
   * Lock background page scroll while the drawer is open. Without this, the
   * <dialog> still lets the user scroll the body underneath on iOS Safari and
   * some Android browsers because the dialog scroll-container is the drawer
   * body, not document. Restored on close.
   */
  #lockBodyScroll() {
    if (document.body.dataset.shpfltScrollLocked === 'true') return;
    document.body.dataset.shpfltScrollLocked = 'true';
    document.body.dataset.shpfltScrollY = String(window.scrollY);
    document.body.style.overflow = 'hidden';
    // touch-action: none on body would block drawer interactions too, so we
    // limit the lock to overflow + position. Scroll-position is restored on
    // unlock to avoid the "jump to top" iOS bug.
  }

  #unlockBodyScroll() {
    if (document.body.dataset.shpfltScrollLocked !== 'true') return;
    document.body.style.overflow = '';
    const y = parseInt(document.body.dataset.shpfltScrollY || '0', 10);
    delete document.body.dataset.shpfltScrollLocked;
    delete document.body.dataset.shpfltScrollY;
    if (Number.isFinite(y) && y > 0) window.scrollTo(0, y);
  }

  /**
   * Wire each `[data-shpflt-locale-toggle]` button to show/hide its sibling
   * `[data-shpflt-locale-list]` list. Buttons live inside the drawer's
   * utility region (per-menu-item, only when a [muted] column is present).
   * Submit is handled by the surrounding `{% form 'localization' %}` —
   * we only manage open/close state + the aria-expanded mirror. */
  #bindLocaleToggles(signal) {
    this.querySelectorAll('[data-shpflt-locale-toggle]').forEach((toggle) => {
      const list = toggle.parentElement?.querySelector('[data-shpflt-locale-list]');
      if (!list) return;
      toggle.setAttribute('aria-expanded', 'false');
      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        const isOpen = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!isOpen));
        list.toggleAttribute('hidden', isOpen);
      }, { signal });
    });
  }

  /**
   * Search icon toggle. Mirrors the legacy section-header pattern (icon
   * reveals/hides an inline `<form action="/search">`) but without jQuery.
   * - Click toggles `[data-search-open]` on the host + reveals the form via CSS.
   * - Focus the input on open (keyboard accessibility).
   * - ESC inside the form (or anywhere with the form open) closes it.
   * - Clicking outside the form closes it.
   * No-op when the search button isn't rendered (enable_search = false). */
  #bindSearchToggle(signal) {
    const toggle = this.querySelector('[data-shpflt-search-toggle]');
    const form = this.querySelector('[data-shpflt-search]');
    const input = this.querySelector('[data-shpflt-search-input]');
    if (!toggle || !form) return;

    const setOpen = (open) => {
      if (open) {
        this.setAttribute('data-search-open', 'true');
        this.#closeAll();
      } else {
        this.removeAttribute('data-search-open');
      }
      toggle.setAttribute('aria-expanded', String(open));
      form.toggleAttribute('hidden', !open);
      if (open) requestAnimationFrame(() => input?.focus());
    };

    toggle.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      setOpen(!this.hasAttribute('data-search-open'));
    }, { signal });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.hasAttribute('data-search-open')) {
        setOpen(false);
        toggle.focus();
      }
    }, { signal });

    document.addEventListener('click', (event) => {
      if (!this.hasAttribute('data-search-open')) return;
      if (form.contains(event.target) || toggle.contains(event.target)) return;
      setOpen(false);
    }, { signal });
  }

  /**
   * Desktop bar locale dropdown. The hidden native <select> is the form's
   * source of truth; the visible button + list mirrors it. Clicking an option
   * writes the iso_code into the select and submits the localization form.
   * Click-outside and ESC close the popup. No-op if the switcher isn't
   * rendered (single-language store or setting disabled).
   */
  #bindBarLocaleDropdown(signal) {
    const wrapper = this.querySelector('[data-shpflt-locale]');
    if (!wrapper) return;
    const trigger = wrapper.querySelector('[data-shpflt-locale-trigger]');
    const menu = wrapper.querySelector('[data-shpflt-locale-menu]');
    const select = this.querySelector('[data-shpflt-locale-input]');
    if (!trigger || !menu || !select) return;
    const form = select.form;

    const setOpen = (open) => {
      trigger.setAttribute('aria-expanded', String(open));
      menu.toggleAttribute('hidden', !open);
    };

    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      setOpen(trigger.getAttribute('aria-expanded') !== 'true');
    }, { signal });

    menu.querySelectorAll('[data-shpflt-locale-option]').forEach((option) => {
      const choose = () => {
        const value = option.dataset.localeValue;
        if (!value || !form) return;
        select.value = value;
        form.submit();
      };
      option.addEventListener('click', choose, { signal });
      option.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          choose();
        }
      }, { signal });
    });

    document.addEventListener('click', (event) => {
      if (trigger.getAttribute('aria-expanded') !== 'true') return;
      if (wrapper.contains(event.target)) return;
      setOpen(false);
    }, { signal });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && trigger.getAttribute('aria-expanded') === 'true') {
        setOpen(false);
        trigger.focus();
      }
    }, { signal });
  }

  /**
   * Toggle `data-at-end` on every horizontal-scroll rail when the user has
   * scrolled to its last item. CSS uses the attribute to drop the right-edge
   * fade-mask so the final item isn't half-faded. Idempotent + cheap (scroll
   * listeners are passive). Re-evaluates on ResizeObserver because the rail's
   * scrollWidth changes when blocks rerender. */
  #bindRailEdgeFade(signal) {
    const rails = this.querySelectorAll(
      '[data-shpflt-drawer-tabs], .shpflt-header__category-bubbles .shpflt-header__chip-rail-list, .shpflt-header__promo-cards[data-layout-desktop="carousel"], .shpflt-header__promo-cards[data-layout-desktop="row_scroll"]'
    );
    const update = (rail) => {
      const max = rail.scrollWidth - rail.clientWidth;
      // 4px slop so subpixel rounding doesn't keep the mask up forever.
      const atEnd = max <= 0 || rail.scrollLeft >= max - 4;
      rail.toggleAttribute('data-at-end', atEnd);
    };
    rails.forEach((rail) => {
      update(rail);
      rail.addEventListener('scroll', () => update(rail), { passive: true, signal });
      if (typeof ResizeObserver !== 'undefined') {
        const ro = new ResizeObserver(() => update(rail));
        ro.observe(rail);
        signal.addEventListener('abort', () => ro.disconnect(), { once: true });
      }
    });
  }

  /**
   * Pointer drag on the knob handle (bottom-sheet style). Translates the
   * drawer with the finger while drag is active and decides on release:
   *   • drag > 30% of drawer height OR velocity > 0.5 px/ms  → close
   *   • otherwise                                            → snap back to 0
   * Reduced-motion just closes on threshold without the snap-back transition.
   */
  #bindKnobDrag(drawer, signal) {
    const knob = this.querySelector('[data-shpflt-drawer-knob]');
    if (!knob || !drawer) return;

    let startY = 0;
    let startTime = 0;
    let lastDy = 0;
    let pointerId = null;

    const onMove = (event) => {
      if (event.pointerId !== pointerId) return;
      const dy = Math.max(0, event.clientY - startY); // never drag upward
      lastDy = dy;
      drawer.style.setProperty('--shpflt-drawer-drag', `${dy}px`);
      event.preventDefault();
    };

    const onEnd = (event) => {
      if (event.pointerId !== pointerId) return;
      const dt = Math.max(1, performance.now() - startTime);
      const velocity = lastDy / dt; // px per ms
      drawer.removeAttribute('data-dragging');
      try { knob.releasePointerCapture(pointerId); } catch { /* released already */ }
      // Pointer move/up/cancel were bound on the knob (the capture target).
      knob.removeEventListener('pointermove', onMove);
      knob.removeEventListener('pointerup', onEnd);
      knob.removeEventListener('pointercancel', onEnd);

      const closeThreshold = drawer.getBoundingClientRect().height * 0.3;
      if (lastDy >= closeThreshold || velocity > 0.5) {
        drawer.close();
      } else {
        drawer.style.setProperty('--shpflt-drawer-drag', '0px');
      }
      // Reset after a tick so the click handler below can read the final dy.
      setTimeout(() => { pointerId = null; }, 0);
    };

    knob.addEventListener('pointerdown', (event) => {
      // Only main pointer (left mouse / single touch). Ignore right-click etc.
      if (event.button !== 0 && event.pointerType === 'mouse') return;
      pointerId = event.pointerId;
      startY = event.clientY;
      startTime = performance.now();
      lastDy = 0;
      drawer.setAttribute('data-dragging', '');
      // setPointerCapture re-targets ALL subsequent pointer events for this
      // pointerId to the knob — so the move/up listeners must live on the
      // knob too, not on window. (That was the original drag-not-working bug.)
      try { knob.setPointerCapture(event.pointerId); } catch { /* unsupported */ }
      knob.addEventListener('pointermove', onMove);
      knob.addEventListener('pointerup', onEnd);
      knob.addEventListener('pointercancel', onEnd);
      event.preventDefault();
    }, { signal });

    // Click on knob (no drag) closes — fallback for AT users who can't drag.
    knob.addEventListener('click', () => {
      if (lastDy > 4) return; // drag-end already handled close/snap
      drawer.close();
    }, { signal });
  }

  /**
   * Move each menu-item bundle's parts from the hidden source div into the
   * real bar nav, drawer tabs, and drawer body. Idempotent — if the source
   * div is absent (e.g. on a re-init), no-ops.
   *
   * Each bundle's child carries a `data-teleport-to` attribute identifying
   * the destination: "nav" | "drawer-tabs" | "drawer-body".
   */
  #teleportMenuItems() {
    const source = this.querySelector('[data-shpflt-blocks-source]');
    if (!source) return;

    const destinations = {
      'nav':          this.querySelector('[data-shpflt-nav]'),
      'drawer-tabs':  this.querySelector('[data-shpflt-drawer-tabs]'),
      'drawer-body':  this.querySelector('[data-shpflt-drawer-body]'),
    };
    if (!destinations.nav || !destinations['drawer-tabs'] || !destinations['drawer-body']) return;

    const bundles = source.querySelectorAll('[data-shpflt-menu-item-bundle]');
    bundles.forEach((bundle) => {
      bundle.querySelectorAll('[data-teleport-to]').forEach((part) => {
        const target = destinations[part.dataset.teleportTo];
        if (target) target.appendChild(part);
      });
    });

    // Drop the SSR placeholder L1 links now that the real block-driven
    // categories have been teleported into the nav. They were rendered
    // server-side so the nav paints before this module runs; from here the
    // interactive categories own the nav. Removing them after the append
    // above avoids any flash of an empty nav between the two states.
    destinations.nav.querySelectorAll('[data-shpflt-ssr-nav]').forEach((placeholder) => placeholder.remove());

    // Activate the first drawer tab + panel so the drawer has a default state.
    const firstTab = destinations['drawer-tabs'].querySelector('[data-shpflt-drawer-tab]');
    if (firstTab) {
      firstTab.setAttribute('aria-selected', 'true');
      firstTab.setAttribute('tabindex', '0');
    }
    const firstPanel = destinations['drawer-body'].querySelector('[data-shpflt-drawer-panel]');
    if (firstPanel) firstPanel.removeAttribute('hidden');

    source.remove();
  }

  /**
   * Move all `[data-shpflt-announcement]` elements from the hidden blocks-source
   * div into `[data-shpflt-promo-bar]`, then unhide the bar if it received items.
   * Respects `data-promo-enabled` (section setting) and applies movement-mode
   * wrappers for marquee and slider animations.
   * MUST be called before #teleportMenuItems() so both teleports complete before
   * any downstream querySelector calls.
   */
  #teleportAnnouncements() {
    const source = this.querySelector('[data-shpflt-blocks-source]');
    const promo = this.querySelector('[data-shpflt-promo-bar]');
    if (!source || !promo) return;

    // Honour the "Activar barra promocional" toggle.
    if (promo.dataset.promoEnabled === 'false') return;

    const items = [...source.querySelectorAll('[data-shpflt-announcement]')];
    if (!items.length) return;

    let movement = promo.dataset.promoMovement || 'fijo_center';

    // Marquee's continuous CSS animation is fully disabled under
    // prefers-reduced-motion (mandatory a11y rule) with no in-between state —
    // left as-is it freezes the track mid-scroll, showing overlapping/
    // concatenated text. Fall back to the same static, JS-rotated single-item
    // display used by the other dynamic modes under reduced motion.
    if (movement === 'marquee' && this.#reducedMotion && items.length > 1) {
      movement = 'slider_fade';
    }

    if (movement === 'marquee') {
      // Wrap items in a scrolling track and duplicate them for a seamless
      // infinite loop. CSS translates the track by -50% (= original length).
      const track = document.createElement('div');
      track.className = 'shpflt-header__promo-track';
      for (const el of items) track.appendChild(el);
      // Clone originals for the second pass (seamless loop).
      for (const el of [...track.children]) {
        const clone = el.cloneNode(true);
        clone.removeAttribute('id');
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
      }
      promo.appendChild(track);
    } else if (
      movement === 'slider_horizontal' ||
      movement === 'slider_vertical' ||
      movement === 'slider_fade'
    ) {
      // Read autoplay duration from the CSS custom property set by Liquid.
      const rawProp = getComputedStyle(this)
        .getPropertyValue('--shpflt-promo-autoplay-seconds')
        .trim();
      const autoplaySec = parseFloat(rawProp) || 4;
      const count = items.length;

      if (movement === 'slider_fade') {
        // Fade: items overlap absolutely; JS cycles active class via interval.
        // Rotation keeps running under prefers-reduced-motion — only the
        // transition itself is skipped, otherwise reduced-motion users would
        // get stuck reading the first message forever.
        promo.style.position = 'relative';
        items.forEach((el, i) => {
          el.style.position = i === 0 ? 'relative' : 'absolute';
          el.style.inset = '0';
          el.style.transition = this.#reducedMotion ? 'none' : 'opacity 400ms ease-in-out';
          el.style.opacity = i === 0 ? '1' : '0';
          el.style.zIndex = i === 0 ? '1' : '0';
          promo.appendChild(el);
        });
        if (count > 1) {
          let current = 0;
          this.#promoAutoplayInterval = setInterval(() => {
            const prev = current;
            current = (current + 1) % count;
            items[prev].style.opacity = '0';
            items[prev].style.zIndex = '0';
            items[current].style.opacity = '1';
            items[current].style.zIndex = '1';
          }, autoplaySec * 1000);
        }
      } else {
        // Slider horizontal / vertical: exactly ONE item visible at a time
        // (100% of the bar), advancing via a translate on a single-axis
        // track. JS drives the active index (autoplay_seconds is a merchant
        // setting, not a fixed value pure CSS @keyframes can parametrize by
        // item count) and CSS only supplies the transition easing — skipped
        // under prefers-reduced-motion so rotation still happens, just instantly.
        const track = document.createElement('div');
        track.className = 'shpflt-header__promo-track';
        if (this.#reducedMotion) track.style.transition = 'none';
        for (const el of items) track.appendChild(el);
        promo.appendChild(track);

        const axisProp = movement === 'slider_horizontal' ? 'X' : 'Y';
        const setIndex = (i) => {
          track.style.transform = `translate${axisProp}(-${i * 100}%)`;
        };
        setIndex(0);

        if (count > 1) {
          let current = 0;
          this.#promoAutoplayInterval = setInterval(() => {
            current = (current + 1) % count;
            setIndex(current);
          }, autoplaySec * 1000);
        }
      }
    } else {
      // fijo_center (default): teleport directly, existing CSS centres each item.
      for (const el of items) promo.appendChild(el);
    }

    promo.hidden = false;
  }

  /**
   * Toggle `data-scrolled`, `data-compact`, `data-hidden` on the host based on
   * scrollY. Powers `position_mode = scroll-aware` (hide on scroll down) and
   * the `compact_on_scroll` / `solid_on_scroll` modes.
   */
  #initScrollBehaviour(signal) {
    const threshold = parseInt(this.dataset.scrollThreshold, 10) || 80;
    const mode = this.dataset.positionMode || 'static';
    const compactOnScroll = this.dataset.compactOnScroll === 'true';
    const scrollAware = mode === 'scroll-aware';

    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      const scrolled = y > threshold;
      this.toggleAttribute('data-scrolled', scrolled);
      if (compactOnScroll) this.toggleAttribute('data-compact', scrolled);

      // Close any open desktop mega-menu when the user scrolls — leaving a
      // panel hanging mid-page is the bug the user described as "el header
      // se queda arriba abierto". Skipped in editor so click-to-inspect works.
      if (Math.abs(y - this.#lastScrollY) > 4 && this.#openCategoryId && !this.#isEditor) {
        this.#closeAll();
      }

      if (scrollAware) {
        // Hide when scrolling down past the threshold, reveal on scroll up.
        const goingDown = y > this.#lastScrollY && y > threshold;
        // Don't hide if the drawer is open (drawer interactions need the bar
        // for orientation). Desktop panels are closed above so anyOpen is now
        // strictly about the dialog.
        const dialogOpen = this.querySelector('dialog[open]');
        this.toggleAttribute('data-hidden', goingDown && !dialogOpen);
      }

      // Compact mode flipping changes the bar height — republish to the body.
      this.#publishHeight();

      this.#lastScrollY = y;
      ticking = false;
    };

    this.#scrollHandler = () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    };
    window.addEventListener('scroll', this.#scrollHandler, { passive: true, signal });
    update(); // initial pass
  }

  /**
   * Observe every `[data-shpflt-transparent-trigger]` element on the page. While
   * one is intersecting the top band, the header is rendered transparent
   * (`data-transparent="true"`). When all triggers are scrolled past, the
   * header reverts to its solid skin.
   *
   * Sections opt in by including:
   *   <span data-shpflt-transparent-trigger aria-hidden="true"></span>
   * at their top edge, gated by a `make_header_transparent` checkbox.
   */
  #initTransparencyObserver() {
    if (this.dataset.enableTransparent !== 'true') return;
    const triggers = document.querySelectorAll('[data-shpflt-transparent-trigger]');
    if (triggers.length === 0) {
      this.removeAttribute('data-transparent');
      return;
    }

    this.#transparentVisibleCount = 0;
    this.#transparentObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#transparentVisibleCount++;
          else this.#transparentVisibleCount = Math.max(0, this.#transparentVisibleCount - 1);
        }
        this.toggleAttribute('data-transparent', this.#transparentVisibleCount > 0);
        // Republish body padding-top: transparent mode opts out of the
        // reservation so the hero can sit underneath the header.
        this.#publishHeight();
      },
      {
        // Trigger-zone: the band right under the top of the viewport, the
        // height of the header bar. When a trigger leaves this band the header
        // turns solid.
        rootMargin: `0px 0px -100% 0px`,
        threshold: 0,
      },
    );
    triggers.forEach((t) => this.#transparentObserver.observe(t));
  }
}

customElements.define('shpflt-header', ShpfltHeader);
